import { ApolloClient, ApolloQueryResult } from '@apollo/client'
import { resolveValue } from 'components/form/final-form/hooks/useField'
import { information } from 'components/modals/information'
import { addDays, differenceInDays, isAfter, isBefore } from 'date-fns'
import { PeriodoGestacionalDocument } from 'graphql/hooks.generated'
import {
  FonteIdadeGestacionalEnum,
  PeriodoGestacionalQuery,
  PeriodoGestacionalQueryVariables,
  ResultadoExamePreNatalInput,
  TrimestreGestacional,
} from 'graphql/types.generated'
import { isUndefinedOrNull } from 'util/checks'
import { dateAsDdMmYyyy, dateAsYyyyMmDd, toDate } from 'util/date/formatDate'
import { MetaPath } from 'util/metaPath'
import { SoapState } from 'view/atendimentos/atendimento-individual/model'

import { DIAS_GESTACAO_LIMITE } from '../../acompanhamentos/puerperio/model-acompanhamentoPuerperio'
import { convertPeriodoGestacionalToIdadeGestacional } from '../aside/pre-natal/converter-acompanhamentoPreNatal'
import { IdadeGestacionalAcompanhamentoPreNatal } from '../aside/pre-natal/model-acompanhamentoPreNatal'
import { Problema } from '../aside/types/ProblemaModel'
import { AvaliacaoFormModel } from '../avaliacao/AvaliacaoForm'
import { ProblemaCondicaoModel } from '../avaliacao/components/problemas-condicoes/model-problemasCondicoes'
import {
  findProblemaByCiapIdCidId,
  findProblemaById,
  findProblemaComCiapW78,
  findProblemaCondicaoNaoEvolucaoGravidezAltoRisco,
} from '../avaliacao/components/problemas-condicoes/utils/operations-problemasCondicoes'
import {
  hasProblemaComCiapW78Ativo,
  hasProblemaCondicaoComCiapW78Ativo,
  hasProblemaCondicaoComCiapW78Automatico,
} from '../avaliacao/components/problemas-condicoes/utils/verifications-preNatal'
import {
  CIAP_GRAVIDEZ_ALTO_RISCO,
  CIAPS_ENCERRAMENTO_GESTACAO,
  CIAPS_GESTACAO_CODIGOS,
  CIAPS_PUERPERIO_CODIGOS,
  CID_GRAVIDEZ_ALTO_RISCO,
  CIDS_ENCERRAMENTO_GESTACAO,
  CIDS_GESTACAO_CODIGOS,
  CIDS_PUERPERIO_CODIGOS,
  FAMILIA_CID_Z34_CODIGO,
  IDADE_GESTACIONAL_MAXIMA_EM_DIAS,
  PreNatalCalculatorStatus,
  QUANTIDADE_DIAS_FIM_PRIMEIRO_TRIMESTRE_GESTACAO,
  QUANTIDADE_DIAS_FIM_SEGUNDO_TRIMESTRE_GESTACAO,
  QUANTIDADE_DIAS_FIM_TERCEIRO_TRIMESTRE_GESTACAO,
  TipoPreNatal,
} from './model-preNatal'

export const getIdadeGestacional = (
  prontuarioId: ID,
  dataAtendimento: Instant,
  apollo: ApolloClient<object>,
  resultadosExamesPreNatalAtendimentoAtual: ResultadoExamePreNatalInput[],
  dum: string,
  fonteIgAtendimentoAtual?: FonteIdadeGestacionalEnum
): Promise<IdadeGestacionalAcompanhamentoPreNatal> =>
  apollo
    .query<PeriodoGestacionalQuery, PeriodoGestacionalQueryVariables>({
      query: PeriodoGestacionalDocument,
      variables: {
        input: {
          prontuarioId,
          dataAtendimento: dateAsYyyyMmDd(dataAtendimento),
          resultadosExamesPreNatalAtendimentoAtual,
          dum,
          fonteIgAtendimentoAtual,
        },
      },
    })
    .then((response: ApolloQueryResult<PeriodoGestacionalQuery>) =>
      convertPeriodoGestacionalToIdadeGestacional(response.data.periodoGestacional)
    )

export const formatIdadeGestacional = (igTotalEmDias: number): string => {
  if (!isUndefinedOrNull(igTotalEmDias)) {
    const semanas = Math.trunc(igTotalEmDias / 7)
    const dias = igTotalEmDias % 7
    return `${'semana'.pluralizeAndConcatValue(semanas)} e ${'dia'.pluralizeAndConcatValue(dias)}`
  } else return null
}

function isCiapPuerperio(codigoCiap?: string) {
  return CIAPS_PUERPERIO_CODIGOS.has(codigoCiap)
}

function isCidPuerperio(codigoCid?: string) {
  return CIDS_PUERPERIO_CODIGOS.has(codigoCid)
}

export function isCiapOuCidDePuerperio(codigoCiap?: string, codigoCid?: string) {
  return (codigoCiap && isCiapPuerperio(codigoCiap)) || (codigoCid && isCidPuerperio(codigoCid))
}

export function isCidFromFamiliaZ34(codigoCid?: string) {
  return FAMILIA_CID_Z34_CODIGO.has(codigoCid)
}

export function isCiapOuCidDeEncerramentoGestacao(codigoCiap?: string, codigoCid?: string) {
  return (codigoCiap && isCiapEncerramentoGestacao(codigoCiap)) || (codigoCid && isCidEncerramentoGestacao(codigoCid))
}

export function isCiapEncerramentoGestacao(codigoCiap?: string) {
  return CIAPS_ENCERRAMENTO_GESTACAO.has(codigoCiap)
}

export function isCidEncerramentoGestacao(codigoCid?: string) {
  return CIDS_ENCERRAMENTO_GESTACAO.has(codigoCid)
}

export function isCiapOuCidDeGestacao(codigoCiap?: string, codigoCid?: string) {
  return (codigoCiap && isCiapGestacao(codigoCiap)) || (codigoCid && isCidGestacao(codigoCid))
}

export function isCiapGestacao(codigoCiap?: string) {
  return CIAPS_GESTACAO_CODIGOS.has(codigoCiap)
}

export function isCidGestacao(codigoCid?: string) {
  return CIDS_GESTACAO_CODIGOS.has(codigoCid)
}

export function isCiapGravidezAltoRisco(codigoCiap?: string) {
  return CIAP_GRAVIDEZ_ALTO_RISCO.has(codigoCiap)
}

export function isCidGravidezAltoRisco(codigoCid?: string) {
  return CID_GRAVIDEZ_ALTO_RISCO.has(codigoCid)
}

export function isDumForaDoLimitePermitido(dataAtendimento: Instant, dum: string): boolean {
  return differenceInDays(dataAtendimento, toDate(dum)) > IDADE_GESTACIONAL_MAXIMA_EM_DIAS
}

export const informationPreNatalQuandoExisteW78ResolvidoNaAvaliacao = () => {
  information({
    title: 'Não é possível iniciar Pré-natal.',
    body: 'Para iniciar Pré-natal, remova a condição W78 com a situação resolvida adicionada na Avaliação.',
  })()
}

export const informationW78ResolvidoNaAvaliacaoQuandoExistePreNatal = () => {
  information({
    title: 'Não é possível resolver a condição de gravidez.',
    body: 'Para resolver a condição de gravidez, remova a condição de Pré-natal.',
  })()
}

export const informationPreNatalSuperiorA336Dias = (dataInicioGestacao: Date) => {
  information({
    title: 'A gestação da cidadã está ativa desde ' + dateAsDdMmYyyy(dataInicioGestacao),
    body:
      'Não é possível dar continuidade a um pré-natal com período superior a ' +
      IDADE_GESTACIONAL_MAXIMA_EM_DIAS +
      ' dias. Para iniciar uma nova gestação, é necessário encerrar a gestação atual através da avaliação do SOAP.',
  })()
}

interface ResetPreNatalValuesProps {
  resetDum(): void
  resetPreNatal(): void
  resetAgendarProximasConsultas(): void
  isPrimeiroAtendimentoPreNatal: boolean
}

export const resetPreNatalValuesToUndefined = ({
  resetDum,
  resetPreNatal,
  resetAgendarProximasConsultas,
  isPrimeiroAtendimentoPreNatal,
}: ResetPreNatalValuesProps) => {
  if (!isPrimeiroAtendimentoPreNatal) {
    resetDum()
  }
  resetPreNatal()
  resetAgendarProximasConsultas()
}

export function getPreNatalCalculatorStatus(
  avaliacao: MetaPath<AvaliacaoFormModel>,
  prevValues: SoapState,
  problemasCondicoesAvaliados: readonly ProblemaCondicaoModel[],
  problemasCondicoesAnteriores: readonly ProblemaCondicaoModel[],
  problemasCidadao?: readonly Problema[]
): PreNatalCalculatorStatus {
  const problemasCondicoesAvaliadosValoresPrevValues =
    resolveValue<ProblemaCondicaoModel[]>(prevValues, avaliacao.problemasECondicoes) ?? []

  const problemaNaoEvolucaoAltoRisco = findProblemaCondicaoNaoEvolucaoGravidezAltoRisco(problemasCondicoesAvaliados)
  const problemaNaoEvolucaoAltoRiscoPrevValue = findProblemaCondicaoNaoEvolucaoGravidezAltoRisco(
    problemasCondicoesAvaliadosValoresPrevValues
  )

  const isAltoRiscoExcluido =
    isUndefinedOrNull(problemaNaoEvolucaoAltoRisco) && !isUndefinedOrNull(problemaNaoEvolucaoAltoRiscoPrevValue)
  const isAltoRiscoAdicionadoEmPrimeiroAtendimentoPreNatal =
    problemaNaoEvolucaoAltoRisco &&
    !problemaNaoEvolucaoAltoRiscoPrevValue &&
    !hasProblemaCondicaoComCiapW78Ativo(problemasCondicoesAnteriores) &&
    !hasProblemaComCiapW78Ativo(problemasCidadao)

  const mustUpdateDataInicioW78Automatico =
    problemaNaoEvolucaoAltoRisco &&
    problemaNaoEvolucaoAltoRiscoPrevValue &&
    problemaNaoEvolucaoAltoRiscoPrevValue.dataInicio !== problemaNaoEvolucaoAltoRisco.dataInicio &&
    hasProblemaCondicaoComCiapW78Automatico(problemasCondicoesAnteriores)

  const mustRemoveW78Automatico = isAltoRiscoExcluido || mustUpdateDataInicioW78Automatico
  const mustAddW78Automatico = isAltoRiscoAdicionadoEmPrimeiroAtendimentoPreNatal || mustUpdateDataInicioW78Automatico

  return {
    mustRemoveW78NosProblemasCondicoesAnteriores: mustRemoveW78Automatico,
    mustAddW78NosProblemasCondicoesAnteriores: mustAddW78Automatico,
    dataInicioProblemaAltoRisco: problemaNaoEvolucaoAltoRisco?.dataInicio?.data,
  }
}

export function getW78(
  problemasCidadao: ReadonlyArray<Problema>,
  problemaCondicaoComW78: ProblemaCondicaoModel
): Problema {
  let problemaW78 =
    findProblemaById(problemasCidadao, problemaCondicaoComW78.problemaId) ??
    findProblemaByCiapIdCidId(problemasCidadao, problemaCondicaoComW78?.ciap?.id, problemaCondicaoComW78?.cid?.id)

  if (!problemaW78) {
    problemaW78 = findProblemaComCiapW78(problemasCidadao)
  }

  return problemaW78
}

export function isInicioOuContinuacaoPreNatal(tipoPreNatal: TipoPreNatal): boolean {
  return (
    tipoPreNatal === TipoPreNatal.PRIMEIRO_ATENDIMENTO_PRE_NATAL || tipoPreNatal === TipoPreNatal.CONTINUACAO_PRE_NATAL
  )
}

export function isEncerramentoGestacao(tipoPreNatal: TipoPreNatal): boolean {
  return tipoPreNatal === TipoPreNatal.W78_RESOLVIDO_NA_AVALIACAO || tipoPreNatal === TipoPreNatal.ENCERRAMENTO_GESTACAO
}

export const calculateTrimestreGestacional = (dataInicioGestacao: Date, dataReferencia: Date): TrimestreGestacional => {
  const dataFimGestacao = addDays(dataInicioGestacao, QUANTIDADE_DIAS_FIM_TERCEIRO_TRIMESTRE_GESTACAO)

  if (isBefore(dataReferencia, dataInicioGestacao) || isAfter(dataReferencia, dataFimGestacao)) {
    return TrimestreGestacional.FORA_PERIODO
  }

  const diasDesdeInicio = differenceInDays(dataReferencia, dataInicioGestacao)

  if (diasDesdeInicio <= QUANTIDADE_DIAS_FIM_PRIMEIRO_TRIMESTRE_GESTACAO) return TrimestreGestacional.PRIMEIRO_TRIMESTRE
  if (diasDesdeInicio <= QUANTIDADE_DIAS_FIM_SEGUNDO_TRIMESTRE_GESTACAO) return TrimestreGestacional.SEGUNDO_TRIMESTRE
  return TrimestreGestacional.TERCEIRO_TRIMESTRE
}

export const isGestacaoExcedida = (dataInicioGestacao: Date, dataReferencia: Instant): boolean => {
  const dataLimite = addDays(dataInicioGestacao, DIAS_GESTACAO_LIMITE)

  return isAfter(dataReferencia, dataLimite)
}
